<template>
  <v-container fluid>
    <v-card dark outlined>
      <v-card-title class="secondary py-1 text-subtitle-1">
        <v-icon left dense>mdi-shield-account</v-icon>
        TSG Admins
        <v-spacer />
        <v-btn x-small dark @click="openCreate">
          <v-icon left x-small>mdi-plus</v-icon>
          Create New Admin
        </v-btn>
      </v-card-title>
      <v-card-text>
        <v-text-field
          v-model="search"
          label="Search username"
          dense
          outlined
          clearable
          hide-details
          class="mb-3"
          style="max-width: 320px"
        />
        <v-data-table
          :headers="headers"
          :items="filtered"
          :loading="loading"
          class="elevation-5"
        >
          <template v-slot:item.Authorised="{ item }">
            {{ item.Authorised ? "True" : "False" }}
          </template>
          <template v-slot:item.actions="{ item }">
            <v-icon small class="mr-2" @click="openEdit(item)">mdi-pencil</v-icon>
            <v-icon small @click="confirmDelete(item)">mdi-delete</v-icon>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <v-dialog v-model="createDialog" max-width="480" persistent>
      <v-card dark>
        <v-card-title>Create New Admin</v-card-title>
        <v-card-text>
          <v-autocomplete
            v-model="createIdentity"
            :items="userOptions"
            item-text="UserId"
            item-value="UserId"
            label="Existing dashboard user"
            dense
            outlined
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="createDialog = false">Cancel</v-btn>
          <v-btn color="secondary" :loading="saving" @click="createAdmin">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="editDialog" max-width="400" persistent>
      <v-card dark>
        <v-card-title>Edit Admin — {{ editItem.Identity }}</v-card-title>
        <v-card-text>
          <v-select
            v-model="editItem.Authorised"
            :items="[
              { text: 'True', value: true },
              { text: 'False', value: false },
            ]"
            label="Authorised"
            dense
            outlined
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="editDialog = false">Cancel</v-btn>
          <v-btn color="secondary" :loading="saving" @click="saveAdmin">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <delete-confirmation-dialog
      v-model="deleteDialog"
      title="Remove this user from TSG Admins?"
      @confirm="deleteAdmin"
      @cancel="deleteDialog = false"
    />
  </v-container>
</template>

<script>
import { mapState } from "vuex";
import { dashboardAPI } from "@/services";
import DeleteConfirmationDialog from "@/components/dialogs/DeleteConfirmationDialog.vue";
import iziToast from "izitoast/dist/js/iziToast.min.js";
import "izitoast/dist/css/iziToast.min.css";

export default {
  name: "TsgAdmins",
  components: { DeleteConfirmationDialog },
  data: () => ({
    loading: false,
    saving: false,
    admins: [],
    userOptions: [],
    search: "",
    headers: [
      { text: "Username", value: "Identity" },
      { text: "Authorised", value: "Authorised" },
      { text: "Actions", value: "actions", sortable: false },
    ],
    createDialog: false,
    createIdentity: null,
    editDialog: false,
    editItem: { Identity: "", Authorised: true },
    deleteDialog: false,
    deleteTarget: null,
  }),
  computed: {
    ...mapState("filter", ["IsAdmin"]),
    filtered() {
      const q = (this.search || "").toLowerCase();
      if (!q) return this.admins;
      return this.admins.filter((a) =>
        (a.Identity || "").toLowerCase().includes(q)
      );
    },
  },
  created() {
    if (!this.IsAdmin) {
      this.$router.replace("/");
      return;
    }
    this.load();
  },
  methods: {
    currentUsername() {
      return (localStorage.getItem("accessUser") || "").trim().toLowerCase();
    },
    isSelf(identity) {
      const me = this.currentUsername();
      return !!me && (identity || "").trim().toLowerCase() === me;
    },
    leaveAdminIfSelf(identity, reason) {
      if (!this.isSelf(identity)) return false;
      this.$store.commit("filter/IsAdmin", false);
      iziToast.info({
        title: "Admins",
        message: reason,
        position: "topRight",
      });
      this.$router.replace("/");
      return true;
    },
    async load() {
      this.loading = true;
      try {
        const api = dashboardAPI.getDashboardAccessAPI();
        const [admins, users] = await Promise.all([
          api.getTsgAdmins(),
          api.getDashboardUsers(),
        ]);
        this.admins = admins.Admins || [];
        this.userOptions = users.Users || [];
      } catch (e) {
        // Self-deauth: next GET is 403 — leave admin UI instead of a dead error state
        if (e?.response?.status === 403) {
          this.$store.commit("filter/IsAdmin", false);
          this.$router.replace("/");
          return;
        }
        iziToast.error({ title: "Admins", message: "Failed to load", position: "topRight" });
      } finally {
        this.loading = false;
      }
    },
    openCreate() {
      this.createIdentity = null;
      this.createDialog = true;
    },
    async createAdmin() {
      this.saving = true;
      try {
        await dashboardAPI.getDashboardAccessAPI().postTsgAdmins({
          Action: "Create",
          Identity: this.createIdentity,
        });
        this.createDialog = false;
        iziToast.success({ title: "Admins", message: "Created", position: "topRight" });
        this.load();
      } catch (e) {
        iziToast.error({
          title: "Admins",
          message: e?.response?.data?.Error || "Create failed",
          position: "topRight",
        });
      } finally {
        this.saving = false;
      }
    },
    openEdit(item) {
      this.editItem = { Identity: item.Identity, Authorised: !!item.Authorised };
      this.editDialog = true;
    },
    async saveAdmin() {
      this.saving = true;
      try {
        await dashboardAPI.getDashboardAccessAPI().postTsgAdmins({
          Action: "Update",
          Identity: this.editItem.Identity,
          Authorised: this.editItem.Authorised,
        });
        this.editDialog = false;
        if (
          this.editItem.Authorised === false &&
          this.leaveAdminIfSelf(
            this.editItem.Identity,
            "Your TSG Admin access was removed"
          )
        ) {
          return;
        }
        iziToast.success({ title: "Admins", message: "Saved", position: "topRight" });
        this.load();
      } catch (e) {
        iziToast.error({
          title: "Admins",
          message: e?.response?.data?.Error || "Save failed",
          position: "topRight",
        });
      } finally {
        this.saving = false;
      }
    },
    confirmDelete(item) {
      this.deleteTarget = item;
      this.deleteDialog = true;
    },
    async deleteAdmin() {
      try {
        const identity = this.deleteTarget?.Identity;
        await dashboardAPI.getDashboardAccessAPI().postTsgAdmins({
          Action: "Delete",
          Identity: identity,
        });
        this.deleteDialog = false;
        if (
          this.leaveAdminIfSelf(
            identity,
            "You were removed from TSG Admins"
          )
        ) {
          return;
        }
        iziToast.success({ title: "Admins", message: "Removed", position: "topRight" });
        this.load();
      } catch (e) {
        iziToast.error({
          title: "Admins",
          message: e?.response?.data?.Error || "Delete failed",
          position: "topRight",
        });
      }
    },
  },
};
</script>
